// src/pages/SubUsuarios/List.tsx
//
// Fidelidade ao artboard `portal-subusuarios.dc.html` (UI-2).
//
// Composição do canvas: `h1` 24/800 + sublinha de 13px com o `.btnpri` "+ Novo
// sub-usuário" à direita; abaixo, o card da grade com `.thead` de 41px e linhas de
// 54px (`GradeCanvas` + `SX_GRADE_CANVAS`, §4.1), colunas Nome / Login / Perfil /
// Situação e a situação em `.chipsoft`.
//
// ONDE O CÓDIGO ESTÁ À FRENTE DO CANVAS — e por isso NÃO regride:
// o artboard desenha à direita um formulário inline de 300px com Nome / Login /
// E-mail / Perfil, como se o portal criasse a conta. O backend
// (`apps/backend-portal/src/usuario/subusuarios-routes.ts`) não faz isso: ele
// VINCULA um usuário que já tem conta, localizado por CPF/CNPJ, com 11 permissões
// booleanas — e nenhum campo de senha. Esse formulário não cabe em 300px nem
// existe no domínio; o fluxo continua no modal (`FormView`), que ganhou as
// primitivas do kit. Registrado no mailbox.

import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';

import { fetcher } from 'src/lib/axios';
import { api } from 'src/auditoria/infra/http/api-client';
import { abrirModal } from 'src/auditoria/utils/abrir-modal';
import { abrirAlerta } from 'src/auditoria/utils/abrir-alerta';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';

import {
  DsNum,
  DsButton,
  GradeCanvas,
  DsPageHeader,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import FormView from './FormView';
import {
  type SubUsuario,
  type SubUsuariosResposta,
  SUB_USUARIOS_KEY,
  getTipoLabel,
  formatCpfCnpj,
} from './types';

// ----------------------------------------------------------------------

/**
 * `SX_GRADE_CANVAS` chega tipado como `SxProps<Theme>`, mas o `TxDataGrid` do
 * portal redeclara a prop como `SxProps` (do `@mui/material`, sem o tema do app),
 * e as duas uniões não se aceitam. A conversão é só de tipo: o objeto é o mesmo.
 */
const { sx: SX_GRADE, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE_PORTAL = SX_GRADE as SxProps;

export default function SubUsuariosListPage() {
  const { data, isLoading } = useSWR<SubUsuariosResposta>(SUB_USUARIOS_KEY, fetcher);

  const columns: GridColDef<SubUsuario>[] = [
    {
      field: 'nome',
      headerName: 'Nome',
      flex: 1.2,
      minWidth: 200,
      // `.trow` do canvas põe a primeira coluna em 700. Não é `.link`: a convenção
      // de §4.4 quer uma coluna clicável por linha, e aqui quem abre o registro é
      // a ação de editar da grade.
      renderCell: (params) => <span style={{ fontWeight: 700 }}>{params.row.nome || '—'}</span>,
    },
    {
      field: 'cpfCnpj',
      headerName: 'Login',
      flex: 1,
      minWidth: 160,
      // `.mono` do artboard na coluna de login.
      renderCell: (params) => <DsNum mono>{formatCpfCnpj(params.row.cpfCnpj)}</DsNum>,
    },
    {
      field: 'tipo',
      headerName: 'Perfil',
      flex: 0.7,
      minWidth: 130,
      renderCell: (params) => <DsStatusChip>{getTipoLabel(params.row.tipo)}</DsStatusChip>,
    },
    {
      field: 'ativo',
      headerName: 'Situação',
      flex: 0.6,
      minWidth: 120,
      // Copy do canvas: o oposto de "Ativo" é "Bloqueado" em `.cs-error`, não um
      // "Inativo" cinza — a própria sublinha da tela fala em bloqueio.
      renderCell: (params) =>
        params.row.ativo ? (
          <DsStatusChip tone="success">Ativo</DsStatusChip>
        ) : (
          <DsStatusChip tone="error">Bloqueado</DsStatusChip>
        ),
    },
  ];

  const handleAdd = () => {
    abrirModal(<FormView />, 'Novo sub-usuário', 'md');
  };

  const handleEdit = (row: SubUsuario) => {
    abrirModal(<FormView subUsuario={row} />, 'Editar sub-usuário', 'md');
  };

  const handleDelete = async (row: SubUsuario) => {
    const confirmado = await abrirAlerta('Remover', 'Deseja remover este sub-usuário?');
    if (!confirmado) return;
    await api.delete(`${SUB_USUARIOS_KEY}/${row.id}`);
    await mutate(SUB_USUARIOS_KEY);
    toast.success('Sub-usuário removido.');
  };

  return (
    <DashboardContent>
      <DsPageHeader
        title="Sub-usuários"
        weight={800}
        subtitle="Acessos auxiliares do cadastro · perfis e bloqueio."
        action={
          <DsButton size="tall" icon="mais" onClick={handleAdd}>
            Novo sub-usuário
          </DsButton>
        }
      />

      {/* O "+" sai da toolbar da grade e vira o `.btnpri` do cabeçalho, como no
          canvas; editar e remover continuam na coluna de ações. */}
      <GradeCanvas>
        <TxDataGrid<SubUsuario>
          rows={data?.subUsuarios ?? []}
          columns={columns}
          loading={isLoading}
          hideFooter
          initialPageSize={100}
          emptyLabel="Nenhum sub-usuário vinculado a este cadastro."
          onEdit={handleEdit}
          onDelete={handleDelete}
          {...PROPS_GRADE}
          sx={SX_GRADE_PORTAL}
        />
      </GradeCanvas>
    </DashboardContent>
  );
}
